import * as React from 'react';
import Joyride, { EVENTS, STATUS } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthService from '../services/auth';

const TOUR_STAGE_KEY = 'mgo_admin_tour_stage_v1';
const TOUR_COMPLETED_KEY = 'mgo_admin_tour_completed_v1';

const TOUR_CONFIG = {
  servicios: {
    path: '/servicios',
    next: '/adminhub',
    nextStage: 'adminhub',
    steps: [
      {
        target: '[data-tour="services-dashboard-card"]',
        title: 'Dashboard administrativo',
        content: 'Desde este acceso administras la configuración completa de tu residencial dentro de la app.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="services-root"]',
        title: 'Servicios',
        content: 'Esta pantalla reúne accesos rápidos para funciones clave. Como admin, aquí tendrás el punto de entrada a configuración.',
      },
    ],
  },
  adminhub: {
    path: '/adminhub',
    next: '/perfil',
    nextStage: 'profile',
    steps: [
      {
        target: '[data-tour="adminhub-quick-actions"]',
        title: 'Operación diaria',
        content: 'Aquí encuentras accesos rápidos a usuarios, recepción, reportes, reservas y otras tareas operativas.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="adminhub-profile-link"]',
        title: 'Tu perfil',
        content: 'Desde aquí puedes entrar a tu perfil para revisar tus datos y preferencias de la app.',
      },
    ],
  },
  profile: {
    path: '/perfil',
    next: null,
    nextStage: null,
    steps: [
      {
        target: '[data-tour="profile-fields"]',
        title: 'Datos personales',
        content: 'Aquí actualizas tu nombre, apellido, teléfono y ajustes personales.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="profile-notifications"]',
        title: 'Notificaciones',
        content: 'Activa aquí las notificaciones para recibir alertas y eventos importantes en la app.',
      },
      {
        target: '[data-tour="profile-darkmode"]',
        title: 'Apariencia',
        content: 'También puedes cambiar el modo visual de la app desde tu perfil.',
      },
    ],
  },
};

export default function AdminOnboarding() {
  const location = useLocation();
  const navigate = useNavigate();
  const [stage, setStage] = React.useState('');
  const [run, setRun] = React.useState(false);

  const refreshStage = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const storedStage = window.sessionStorage.getItem(TOUR_STAGE_KEY) || '';
    setStage(storedStage);
    return storedStage;
  }, []);

  React.useEffect(() => {
    refreshStage();
  }, [refreshStage]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (!AuthService.userRoleIncludes(['admin'])) {
      setRun(false);
      return undefined;
    }
    const currentStage = window.sessionStorage.getItem(TOUR_STAGE_KEY) || '';
    const currentConfig = TOUR_CONFIG[currentStage];
    if (!currentConfig || currentConfig.path !== location.pathname) {
      setRun(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setRun(true);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const handleAdvance = React.useCallback((currentStage) => {
    const config = TOUR_CONFIG[currentStage];
    if (!config) {
      return;
    }
    setRun(false);
    if (typeof window === 'undefined') {
      return;
    }
    if (config.nextStage && config.next) {
      window.sessionStorage.setItem(TOUR_STAGE_KEY, config.nextStage);
      setStage(config.nextStage);
      navigate(config.next);
      return;
    }
    window.sessionStorage.removeItem(TOUR_STAGE_KEY);
    window.sessionStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    setStage('');
  }, [navigate]);

  const handleJoyrideCallback = React.useCallback((data) => {
    const finished = data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED;
    const targetMissing = data.type === EVENTS.TARGET_NOT_FOUND && data.action === 'next';
    if (!finished && !targetMissing) {
      return;
    }
    if (!stage) {
      return;
    }
    handleAdvance(stage);
  }, [handleAdvance, stage]);

  const steps = stage ? TOUR_CONFIG[stage]?.steps || [] : [];

  if (!stage || !steps.length) {
    return null;
  }

  return (
    <Joyride
      run={run}
      steps={steps}
      continuous
      scrollToFirstStep
      showProgress
      showSkipButton
      disableScrolling={false}
      spotlightClicks
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#238048',
          zIndex: 1500,
        },
      }}
      locale={{
        back: 'Atrás',
        close: 'Cerrar',
        last: 'Finalizar',
        next: 'Siguiente',
        open: 'Abrir',
        skip: 'Omitir',
      }}
    />
  );
}
